import React, { useState } from 'react';

import { Box, Group, Paper, Text, createStyles, Divider, px } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import KiloAlert from 'components/KiloAlert';
import Menu from 'components/Menu';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { AthletePackageHold, AthletePackageRetrieve } from 'types';

import DeleteAthletePackageHoldModal from './DeleteAthletePackageHoldModal';
import EditAthletePackageHoldModal from './EditAthletePackageHoldModal';

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    rowGap: theme.spacing.xs,
    alignItems: 'center',
    textAlign: 'center',
    gridTemplateColumns: '2fr 2fr 2fr 1fr',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
    [theme.fn.smallerThan('sm')]: {
      fontSize: theme.fontSizes.sm,
    },
  },
  divider: {
    gridColumnStart: '1',
    gridColumnEnd: '5',
  },
}));

const AthletePackageHoldsCard = ({
  athletePackage,
}: {
  athletePackage: AthletePackageRetrieve;
}): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { dateFormat } = useLocalization();

  const [isEditModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [selected, setSelected] = useState<AthletePackageHold | undefined>();

  const handleEdit = (athletePackageHold: AthletePackageHold): void => {
    setSelected(athletePackageHold);
    openEditModal();
  };

  const handleCloseEdit = (): void => {
    setSelected(undefined);
    closeEditModal();
  };

  const handleDelete = (athletePackageHold: AthletePackageHold): void => {
    setSelected(athletePackageHold);
    openDeleteModal();
  };

  // A scheduled (future) cancellation blocks hold operations: the user must remove it
  // before modifying holds, since we can't know how the hold should shift it (DEV-2807).
  const hasScheduledCancellation =
    !!athletePackage.cancellationDate && athletePackage.status !== 'CANCELLED';
  const disableHoldActions = athletePackage.status === 'CANCELLED' || hasScheduledCancellation;

  const getActionMenu = (aph: AthletePackageHold): JSX.Element => {
    const items = [
      {
        title: 'Modify',
        onClick: () => handleEdit(aph),
        icon: <IconEdit size={theme.fontSizes.sm} />,
        disabled: disableHoldActions,
      },
      {
        title: 'Remove',
        onClick: () => handleDelete(aph),
        icon: <IconTrashX size={theme.fontSizes.sm} />,
        color: 'red',
        disabled: disableHoldActions,
      },
    ];

    return (
      <Group position='right'>
        <Menu position='bottom-end' items={items} data-cy='holdsActionMenu' />
      </Group>
    );
  };

  return (
    <>
      <Paper px='lg' py='md' shadow='xs' radius='md' withBorder>
        <Text size={isDesktop ? 'lg' : 'md'} fw='bold' mb='xs'>
          Pauses
        </Text>
        {hasScheduledCancellation && (
          <Box mb='md'>
            <KiloAlert w='100%' variant='filled' variantStyle='warning'>
              Pauses can&apos;t be changed while a cancellation is scheduled. Remove the scheduled
              cancellation first.
            </KiloAlert>
          </Box>
        )}
        <div className={classes.grid}>
          <Text color='dimmed' fs='italic'>
            Start Date
          </Text>
          <Text color='dimmed' fs='italic'>
            End Date
          </Text>
          <Text color='dimmed' fs='italic'>
            Reason
          </Text>
          <div />
        </div>

        <Divider my={px(theme.spacing.xs) / 2} />

        <div className={classes.grid}>
          {athletePackage.athletePackageHolds.map(aph => (
            <>
              <Text key={`${aph.athletePackageHoldId}-start`}>
                {dayjs(aph.startDate).format(dateFormat)}
              </Text>
              <Text key={`${aph.athletePackageHoldId}-end`}>{dayjs(aph.endDate).format(dateFormat)}</Text>
              <Text key={`${aph.athletePackageHoldId}-reason`}>{aph.reason || 'None'}</Text>
              {getActionMenu(aph)}
            </>
          ))}
        </div>
      </Paper>
      <EditAthletePackageHoldModal
        opened={isEditModalOpened}
        athletePackage={athletePackage}
        closeHandler={handleCloseEdit}
        athletePackageHold={selected}
      />
      <DeleteAthletePackageHoldModal
        opened={!!selected && deleteModalOpened}
        handleClose={closeDeleteModal}
        athletePackageHold={selected}
      />
    </>
  );
};

export default AthletePackageHoldsCard;
